import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { ArrowUpRight, Copy, Check } from "lucide-react";

/**
 * Iter 169 — Public "How to use VG" guide.
 *
 * Single page, two guides behind a role toggle (Player / Streamer).
 * Copy is the final approved brand copy — reproduced verbatim; the
 * casino wager-request template block is English-only per legal (the
 * surrounding intro is EN + DE like the rest of the page).
 *
 * Guardrails: static content, no backend, no data collection, no color
 * or logo changes. Reuses the site's existing dark theme + Tailwind
 * primitives. Readable width capped at max-w-3xl. Both roles + both
 * languages must render cleanly.
 */

// Iter 189 — CASINO template synced to the canonical Iter-189 wording
// (drops the "[Casino]" placeholder so onboarding + guide ship exactly
// the same bytes to clipboard).
const CASINO_TEMPLATE = `Subject: Request for my wagering activity statement (last 3 months)

Dear Support Team,

I would like to request a detailed statement of my wagering activity for my account (username: [your username]) covering the last 3 months.

Please provide my total wagered / turnover amount broken down by the following categories:
1. Slots
2. Live Casino
3. Table Games
4. Sports
5. House / Originals (in-house games, e.g. crash, plinko, mines)
6. Dice

If a full category breakdown is not available, please provide as much detail as possible, plus the total wagered amount for the period.

Kindly send this as a document, export, or screenshot that I can download. Thank you very much for your help.

Best regards,
[your name]`;

// Iter 189 — Case-site variant of the support-request template.
const CASE_TEMPLATE = `Subject: Request for my wagering activity statement (last 3 months)

Dear Support Team,

I would like to request a detailed statement of my activity for my account (username: [your username]) covering the last 3 months.

Please provide my total wagered / turnover amount for the period — i.e. the total value of case / pack openings (or equivalent wagering) on my account over the last 3 months.

If a more detailed breakdown is available, please include it; otherwise the total wagered amount for the period is sufficient.

Kindly send this as a document, export, or screenshot that I can download. Thank you very much for your help.

Best regards,
[your name]`;

const PLAYER_STEPS = ["p1", "p2", "p3", "p4", "p5", "p6", "p7", "p8"];
const STREAMER_STEPS = ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"];

function Step({ index, title, body, testid }) {
  return (
    <li className="grid grid-cols-[auto_1fr] gap-5" data-testid={testid}>
      <div className="w-10 h-10 shrink-0 border border-white/20 flex items-center justify-center font-mono text-sm text-[#2DD4A7]">
        {String(index).padStart(2, "0")}
      </div>
      <div>
        <h3 className="font-display font-bold text-lg text-[#E8ECF4] mb-1.5">{title}</h3>
        <p className="text-sm text-[#8B97AE] leading-relaxed">{body}</p>
      </div>
    </li>
  );
}

function TemplateBlock({ t, template, titleKey, introKey, langNoteKey, testidPrefix }) {
  // Iter 189 — Generic template-block. Renders one titled section with an
  // intro line, the raw template body, and a Copy button that toggles a
  // "Copied" confirmation. Casino + case-site blocks both use this.
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(template);
      } else {
        // Fallback for older browsers / non-secure contexts.
        const ta = document.createElement("textarea");
        ta.value = template;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success(t("howto.tpl.copied"));
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error(t("howto.tpl.copyFailed"));
    }
  };
  return (
    <section className="mt-16 border border-white/10 p-6 lg:p-8" data-testid={testidPrefix}>
      <div className="flex items-baseline justify-between gap-4 mb-3">
        <h2 className="font-display font-black text-2xl tracking-tight text-[#E8ECF4]">
          {t(titleKey)}
        </h2>
        <button
          type="button"
          onClick={copy}
          data-testid={`${testidPrefix}-copy`}
          className="shrink-0 inline-flex items-center gap-1.5 border border-[#2DD4A7]/50 bg-[#2DD4A7]/[0.08] hover:bg-[#2DD4A7]/[0.16] text-[#2DD4A7] px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? t("howto.tpl.copied") : t("howto.tpl.copyBtn")}
        </button>
      </div>
      <p className="text-sm text-[#8B97AE] leading-relaxed mb-5">{t(introKey)}</p>
      <pre
        className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-[#E8ECF4] bg-[#151B28] border border-white/8 p-4 lg:p-5 overflow-x-auto"
        data-testid={`${testidPrefix}-body`}
      >
        {template}
      </pre>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[#8B97AE]" data-testid={`${testidPrefix}-lang-note`}>
        {t(langNoteKey)}
      </p>
    </section>
  );
}

function CasinoTemplateBlock({ t }) {
  return (
    <TemplateBlock
      t={t}
      template={CASINO_TEMPLATE}
      titleKey="howto.tpl.title"
      introKey="howto.tpl.intro"
      langNoteKey="howto.tpl.langNote"
      testidPrefix="howto-tpl"
    />
  );
}

function CaseTemplateBlock({ t }) {
  return (
    <TemplateBlock
      t={t}
      template={CASE_TEMPLATE}
      titleKey="howto.tpl.caseTitle"
      introKey="howto.tpl.caseIntro"
      langNoteKey="howto.tpl.caseLangNote"
      testidPrefix="howto-tpl-case"
    />
  );
}

function PlayerGuide({ t }) {
  return (
    <>
      <ol className="space-y-8" data-testid="howto-player-steps">
        {PLAYER_STEPS.map((k, i) => (
          <Step
            key={k}
            index={i + 1}
            title={t(`howto.player.${k}.title`)}
            body={t(`howto.player.${k}.body`)}
            testid={`howto-player-${k}`}
          />
        ))}
      </ol>

      <section className="mt-16 border-t border-white/10 pt-10" data-testid="howto-player-earn">
        <h2 className="font-display font-black text-2xl lg:text-3xl tracking-tight mb-3">
          {t("howto.player.earn.title")}
        </h2>
        <p className="text-sm text-[#8B97AE] leading-relaxed mb-5">{t("howto.player.earn.body")}</p>
        <p className="text-sm text-[#E8ECF4] leading-relaxed mb-3">{t("howto.player.earn.examplesLead")}</p>
        <ul className="space-y-2.5 mb-5 pl-1">
          <li className="text-sm text-[#8B97AE] leading-relaxed">
            <span className="text-[#F5B841] font-mono text-[11px] uppercase tracking-widest mr-2">·</span>
            {t("howto.player.earn.casino")}
          </li>
          <li className="text-sm text-[#8B97AE] leading-relaxed">
            <span className="text-[#F5B841] font-mono text-[11px] uppercase tracking-widest mr-2">·</span>
            {t("howto.player.earn.case")}
          </li>
        </ul>
        <p className="text-sm text-[#8B97AE] leading-relaxed italic">{t("howto.player.earn.note")}</p>
      </section>

      <section className="mt-16 border-t border-white/10 pt-10" data-testid="howto-player-invite">
        <h2 className="font-display font-black text-2xl lg:text-3xl tracking-tight mb-3">
          {t("howto.player.invite.title")}
        </h2>
        <p className="text-sm text-[#8B97AE] leading-relaxed">{t("howto.player.invite.body")}</p>
      </section>

      <CasinoTemplateBlock t={t} />
      <CaseTemplateBlock t={t} />
    </>
  );
}

function StreamerGuide({ t }) {
  return (
    <>
      <ol className="space-y-8" data-testid="howto-streamer-steps">
        {STREAMER_STEPS.map((k, i) => (
          <Step
            key={k}
            index={i + 1}
            title={t(`howto.streamer.${k}.title`)}
            body={t(`howto.streamer.${k}.body`)}
            testid={`howto-streamer-${k}`}
          />
        ))}
      </ol>

      {/* Iter 172 — Refund policy disclaimer. Mirrors the notice inside
          the Buy Credits section verbatim so streamers see it BOTH at
          purchase time and while reading the guide. Display text only —
          no policy enforcement here; the credit-only refund rule is
          already codified in the settlement + top-up code paths. */}
      <section
        className="mt-16 border border-[#F5B841]/40 bg-[#F5B841]/[0.06] p-6"
        data-testid="howto-streamer-refund-disclaimer"
      >
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] mb-2">
          {t("howto.streamer.refundDisclaimer.title")}
        </div>
        <p className="text-sm text-[#E8ECF4] leading-relaxed">
          {t("howto.streamer.refundDisclaimer.body")}
        </p>
      </section>
    </>
  );
}

export default function HowItWorks() {
  const { t } = useI18n();
  const [role, setRole] = useState("player"); // default per spec

  return (
    <article className="max-w-3xl mx-auto px-6 py-16 lg:py-20">
      <div className="font-mono text-[11px] uppercase tracking-widest text-[#2DD4A7] mb-3">
        {t("howto.eyebrow")}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-4" data-testid="howto-title">
        {t("howto.title")}
      </h1>
      <p className="text-sm text-[#8B97AE] leading-relaxed mb-8 max-w-2xl">{t("howto.intro")}</p>

      {/* Role toggle */}
      <div
        role="tablist"
        aria-label={t("howto.title")}
        className="inline-flex border border-white/15 mb-12 select-none"
        data-testid="howto-toggle"
      >
        <button
          type="button"
          role="tab"
          aria-selected={role === "player"}
          onClick={() => setRole("player")}
          className={`px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest transition-colors ${
            role === "player"
              ? "bg-[#2DD4A7] text-[#04150F]"
              : "text-[#E8ECF4] hover:bg-white/5"
          }`}
          data-testid="howto-toggle-player"
        >
          {t("howto.toggle.player")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={role === "streamer"}
          onClick={() => setRole("streamer")}
          className={`px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest border-l border-white/15 transition-colors ${
            role === "streamer"
              ? "bg-[#2DD4A7] text-[#04150F]"
              : "text-[#E8ECF4] hover:bg-white/5"
          }`}
          data-testid="howto-toggle-streamer"
        >
          {t("howto.toggle.streamer")}
        </button>
      </div>

      {role === "player" ? <PlayerGuide t={t} /> : <StreamerGuide t={t} />}

      {/* Bottom CTA — cross-links the OTHER role's registration */}
      <div className="mt-20 border-t border-white/10 pt-10 flex flex-wrap items-center gap-3" data-testid="howto-cta-row">
        <Link
          to={role === "player" ? "/register/player" : "/register/streamer"}
          data-testid="howto-cta-primary"
          className="inline-flex items-center gap-1.5 bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-11 px-5 font-semibold text-sm transition-colors"
        >
          {role === "player" ? t("landing.cta.player") : t("landing.cta.streamer")}
          <ArrowUpRight className="w-4 h-4" />
        </Link>
        <Link
          to="/faq"
          data-testid="howto-cta-faq"
          className="inline-flex items-center gap-1.5 border border-white/20 text-white hover:bg-white/10 h-11 px-5 font-semibold text-sm transition-colors"
        >
          {t("footer.faq")}
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </article>
  );
}
